import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Loader2 } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { Kicker } from "@/components/primitives";
import { apiClient, formatApiError } from "@/lib/auth";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!email.trim()) { setErr("Please enter your email."); return; }
    setBusy(true);
    try {
      await apiClient.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail, e2.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-5 py-8" data-testid="forgot-page">
      <Link to="/auth" className="self-start chip mb-6 max-w-3xl w-full sm:w-auto" data-testid="forgot-back">
        <ArrowLeft size={14} /> Back to sign in
      </Link>

      <div className="h-card w-full max-w-md px-6 sm:px-10 py-10 shadow-seal animate-bloom-in" data-testid="forgot-card">
        <div className="flex justify-center mb-5"><BrandLogo size={80} /></div>
        <Kicker>Password Reset</Kicker>
        <h1 className="font-display text-4xl sm:text-5xl text-sage-dark text-center mt-3 leading-tight">
          Lost your way?
        </h1>
        <p className="text-center font-display italic text-ink-soft mt-2">
          We&apos;ll send you a fresh link, valid for one hour.
        </p>

        {sent ? (
          <div className="mt-8 text-center space-y-4" data-testid="forgot-sent">
            <p className="text-ink leading-relaxed">
              If <span className="font-semibold">{email}</span> matches an account, a reset link is on its way.
            </p>
            <p className="text-sm text-ink-soft">Have a peek in your inbox — and the junk folder, just in case.</p>
            <Link to="/auth" className="btn-heritage mt-4 inline-flex" data-testid="forgot-back-to-signin">
              Back to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-3" data-testid="forgot-form">
            <label className="block">
              <span className="text-sm text-ink-soft mb-1.5 inline-block">Your email</span>
              <input
                type="email"
                className="field"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="forgot-email"
                autoComplete="email"
                required
              />
            </label>
            {err && (
              <div className="bg-cream-deep border border-heritage-border rounded-xl px-4 py-3 text-sm text-terracotta-dark" data-testid="forgot-error">
                {err}
              </div>
            )}
            <button
              type="submit"
              className="btn-heritage w-full mt-2"
              disabled={busy}
              data-testid="forgot-submit"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
              {busy ? "Sending…" : "Send reset link"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
